/**
 * spaceContext.js — tiny helper around the active role-space.
 *
 * Two distinct "space" axes (separate localStorage keys so they don't
 * collide if a future writer-becomes-admin migration ever happens):
 *
 *  • writerActiveSpace:  'writer' | 'reader'  — scribes flip between
 *                         their own dashboard and consuming as a reader.
 *  • adminViewingAs:     'admin'  | 'reader' | 'writer' — admins can
 *                         view the app AS a reader or writer to QA the
 *                         experience without losing god-mode access.
 *
 * AppShell composes the persisted choice with the user's real role to
 * derive the "effective role" used for navigation, badges, CTAs, and
 * the bold viewing-as banner.
 */

const WRITER_SPACE_KEY = 'writerActiveSpace';
const ADMIN_AS_KEY = 'adminViewingAs';
const EVT = 'space-changed';

// ── Writer space (existing) ────────────────────────────────────────────────

/** Resolve the persisted writer space, defaulting to 'writer'. */
export function getActiveSpace() {
  try {
    const v = localStorage.getItem(WRITER_SPACE_KEY);
    return v === 'reader' ? 'reader' : 'writer';
  } catch {
    return 'writer';
  }
}

/** Persist a new writer space and notify listeners. */
export function setActiveSpace(next) {
  const val = next === 'reader' ? 'reader' : 'writer';
  try {
    localStorage.setItem(WRITER_SPACE_KEY, val);
  } catch {
    // localStorage unavailable — silently continue.
  }
  try {
    window.dispatchEvent(new CustomEvent(EVT, { detail: { space: val, axis: 'writer' } }));
  } catch {
    // CustomEvent unsupported — ignore.
  }
  return val;
}

// ── Admin "viewing as" (new) ───────────────────────────────────────────────

/** Read the persisted admin viewing-as mode. Defaults to 'admin'. */
export function getAdminViewingAs() {
  try {
    const v = localStorage.getItem(ADMIN_AS_KEY);
    if (v === 'reader' || v === 'writer') return v;
    return 'admin';
  } catch {
    return 'admin';
  }
}

/** Set the admin viewing-as mode and notify listeners. */
export function setAdminViewingAs(next) {
  const val = next === 'reader' ? 'reader' : next === 'writer' ? 'writer' : 'admin';
  try {
    localStorage.setItem(ADMIN_AS_KEY, val);
  } catch {
    // ignore
  }
  try {
    window.dispatchEvent(new CustomEvent(EVT, { detail: { space: val, axis: 'admin' } }));
  } catch {
    // ignore
  }
  return val;
}

// ── Subscriptions ──────────────────────────────────────────────────────────

/** Subscribe to ANY space change (writer or admin). Returns unsubscribe. */
export function onSpaceChange(handler) {
  const wrapped = (e) => handler(e?.detail?.space || getActiveSpace(), e?.detail?.axis);
  window.addEventListener(EVT, wrapped);
  return () => window.removeEventListener(EVT, wrapped);
}

/**
 * Given the user's real role and persisted spaces, compute the
 * "effective role" used to pick navigation / badges / CTAs.
 *
 *   writer + reader-space → behaves like a plain reader
 *   admin  + viewing reader → behaves like a plain reader
 *   admin  + viewing writer → behaves like a scribe
 *   admin  + viewing admin (default) → admin god mode
 *
 * Real role still controls what API endpoints the user can hit; this
 * helper only drives UX.
 */
export function effectiveRoleFor(realRole, space = getActiveSpace()) {
  if (realRole === 'admin') {
    const viewing = getAdminViewingAs();
    if (viewing === 'reader') return 'user';
    if (viewing === 'writer') return 'writer';
    return 'admin';
  }
  // Co-admins/Kings ride the SAME admin viewing-as axis so they can flip
  // between their read-only admin panel and previewing the app as a
  // reader/writer. Effective role stays 'king' unless they explicitly swap.
  // Feb 12 2026: 'coadmin' was renamed to 'king' — accept both for legacy.
  if (realRole === 'coadmin' || realRole === 'king') {
    const viewing = getAdminViewingAs();
    if (viewing === 'reader') return 'user';
    if (viewing === 'writer') return 'writer';
    return realRole;
  }
  if (realRole === 'writer' && space === 'reader') return 'user';
  return realRole || 'user';
}
